import { config } from '../../config';
import { StorageError } from '../../app/errors';
import { ARTIFACT_PHASE } from '../../types';
import type { ArtifactPhase, MatchRow, TalkPointRow, TalkPointSelection } from '../../types';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';

/**
 * `talkPoints` — INV-6 and INV-13.
 *
 * ── Rank is never incremented ────────────────────────────────────────────────
 *
 * Design §10.2: *"`TalkPoint.rank` is derived from selection order, never incremented."* A "next
 * free rank" scheme yields `{1,2,4}` after a single promote → un-promote → re-promote cycle, and
 * `CompleteDistillation`'s INV-6 postcondition then aborts with the match stuck in `distill` and no
 * explanation the user could act on.
 *
 * So this module exports **no way to set a rank.** `writeSelection` takes the ordered list the user
 * sees and rewrites the whole `(matchId, phase)` set as `1..n` in one transaction. A gap is not
 * something you have to avoid producing; it is unrepresentable. Rank is also CARD-1's delivery
 * order, so the list order the user sees is the order the card renders.
 *
 * ── INV-13 ───────────────────────────────────────────────────────────────────
 *
 * `TalkPointRow` is a discriminated union: a `fullTime` row without `instructionText` does not
 * typecheck. `assertInv13` is the runtime half, for rows arriving from an import (design §4).
 */

/**
 * One entry of the distillation surface's ordered selection list, without its rank.
 *
 * **Defined in `types/`**, not here, since U-13: `domain/commands`'s `writeTalkPointSelection` op
 * names it, and NFR-6 forbids `domain/` from importing an I/O layer. Re-exported so this module's
 * existing callers are unaffected — one definition, two import paths, no second copy to drift.
 */
export type { TalkPointSelection };

/**
 * Rewrites every TalkPoint of one `(matchId, phase)` to match the ordered selection.
 *
 * Rows previously in the set and absent from `ordered` are deleted; every survivor is written with
 * `rank = index + 1`. Deletion happens **first**, because `&[matchId+phase+rank]` is unique and
 * IndexedDB enforces uniqueness per `put`: re-ranking in place would collide with the rows still
 * holding those ranks, exactly as a board swap does (§3.4).
 *
 * @throws {StorageError} outside a transaction, above `TALKPOINT_COUNT` entries, on an INV-13
 *   violation, or on a write failure.
 */
export async function writeSelection(
  matchId: string,
  phase: ArtifactPhase,
  ordered: readonly TalkPointSelection[],
): Promise<void> {
  requireTransaction('writeSelection');

  // INV-6 bounds both phases: `fullTime` is exactly TALKPOINT_COUNT at archive time, `halftime` is
  // "unconstrained in count from 0 to TALKPOINT_COUNT". Neither may exceed it at any moment.
  if (ordered.length > config.TALKPOINT_COUNT) {
    throw new StorageError(
      `INV-6: a match may hold at most ${config.TALKPOINT_COUNT} ${phase} talk points; the selection held ${ordered.length}.`,
      { context: { store: TABLE.talkPoints, matchId, phase, count: ordered.length } },
    );
  }
  for (const entry of ordered) {
    if (entry.matchId !== matchId || entry.phase !== phase) {
      throw new StorageError(
        `Talk point ${entry.id} does not belong to the ${phase} selection of match ${matchId}.`,
        { context: { store: TABLE.talkPoints, id: entry.id, matchId, phase } },
      );
    }
    assertInv13(entry);
  }

  const table = db().talkPoints;
  const existing = await listForMatch(matchId, phase);

  try {
    // Clear the whole `(matchId, phase)` set first, then write `1..n`. Not an optimization to be
    // improved into a partial rewrite: `&[matchId+phase+rank]` is unique and IndexedDB enforces
    // uniqueness per `put`, so re-ranking in place collides with rows still holding those ranks —
    // and reasoning about "which ranks are free" is the next-free-rank trap under another name.
    await table.bulkDelete(existing.map((row) => row.id));
    for (const [index, entry] of ordered.entries()) {
      const rank = index + 1;
      await table.add(
        entry.phase === ARTIFACT_PHASE.fullTime ? { ...entry, rank } : { ...entry, rank },
      );
    }
  } catch (cause) {
    throw new StorageError('Could not save the talk points.', {
      cause,
      context: { store: TABLE.talkPoints, matchId, phase, count: ordered.length },
    });
  }
}

/** INV-8's deletion path. Named for its single caller. @throws {StorageError} */
export async function deleteTalkPointsForCascade(ids: readonly string[]): Promise<void> {
  requireTransaction('deleteTalkPointsForCascade');
  if (ids.length === 0) return;
  try {
    await db().talkPoints.bulkDelete([...ids]);
  } catch (cause) {
    throw new StorageError('Could not delete the talk points during a cascade.', {
      cause,
      context: { store: TABLE.talkPoints, count: ids.length },
    });
  }
}

/**
 * INV-13's runtime half: *"`instructionText` is required for `phase=fullTime` rows."*
 *
 * The union makes it a compile error in this codebase; this closes the import path, where a row
 * arrives from a file and no compiler has seen it (design §4, BAK-2's "validates INV-6 and INV-13 at
 * the repo boundary and quarantines violating rows").
 *
 * @throws {StorageError} on a `fullTime` row with no instruction text.
 */
export function assertInv13(row: TalkPointSelection | TalkPointRow): void {
  if (row.phase !== ARTIFACT_PHASE.fullTime) return;
  if (typeof row.instructionText === 'string' && row.instructionText.trim().length > 0) return;
  throw new StorageError(
    `INV-13: a full-time talk point needs instruction text; ${row.id} has none. A share card with a blank point is not a talk point.`,
    { context: { store: TABLE.talkPoints, id: row.id, matchId: row.matchId } },
  );
}

/**
 * INV-6's postcondition: *"exactly `TALKPOINT_COUNT` TalkPoint rows with `phase=fullTime` and ranks
 * {1,2,3} exist for a match in `archived` state that was not abandoned."*
 *
 * One function, three callers — `CompleteDistillation`, the BAK-2 import path, and OBS-2's integrity
 * report. Design §4 is explicit that an invariant enforced only by a UI (DIST-2) is not an invariant
 * (§19 F-30), and three copies of this check is how the import path's copy ends up laxer than the
 * command's.
 *
 * @throws {StorageError} when the match would be archivable with the wrong number or ranks. Thrown
 *   inside the caller's transaction, so the archive aborts and nothing commits.
 */
export async function assertInv6(match: MatchRow): Promise<void> {
  if (match.abandoned) return;
  const rows = await listForMatch(match.id, ARTIFACT_PHASE.fullTime);
  const ranks = rows.map((row) => row.rank).sort((a, b) => a - b);
  const expected = Array.from({ length: config.TALKPOINT_COUNT }, (_, i) => i + 1);
  const matches =
    ranks.length === expected.length && ranks.every((rank, i) => rank === expected[i]);
  if (!matches) {
    throw new StorageError(
      `INV-6: match ${match.id} must hold exactly ${config.TALKPOINT_COUNT} full-time talk points ranked ${expected.join(', ')}; it holds ranks [${ranks.join(', ')}].`,
      { context: { store: TABLE.talkPoints, matchId: match.id, ranks } },
    );
  }
  for (const row of rows) assertInv13(row);
}

// ─────────────────────────────────────────────────────────────────────────────
// Reads
// ─────────────────────────────────────────────────────────────────────────────

/** In rank order, which is CARD-1's delivery order. @throws {StorageError} */
export async function listForMatch(
  matchId: string,
  phase?: ArtifactPhase,
): Promise<TalkPointRow[]> {
  try {
    const rows = await db().talkPoints.where('matchId').equals(matchId).toArray();
    return rows
      .filter((row) => phase === undefined || row.phase === phase)
      .sort((a, b) => a.rank - b.rank);
  } catch (cause) {
    throw new StorageError('Could not read the talk points.', {
      cause,
      context: { store: TABLE.talkPoints, matchId, phase },
    });
  }
}
